/**
 * Guided "Automate onboarding" wizard — static step definitions and transitions.
 * The UI advances steps locally; the architect is called only when the user submits the full plan.
 */

export const ONBOARDING_WIZARD_ID = "automate-onboarding";

export const ONBOARDING_WIZARD_START_OPTION_ID = "c-pe-1";

/** sessionStorage key for rate-limit / progress recovery */
export const ONBOARDING_WIZARD_STORAGE_KEY = "idwiz_onboarding_wizard_v1";

export const ONBOARDING_SOURCE_OPTIONS = [
  {
    id: "onboarding-wizard-source-ad",
    value: "ad",
    label: "Active Directory",
    description: "Okta AD Agent, delegated auth, directory import",
  },
  {
    id: "onboarding-wizard-source-hris",
    value: "hris",
    label: "HRIS",
    description: "Workday, BambooHR, or similar HR system of record",
  },
  {
    id: "onboarding-wizard-source-csv",
    value: "csv",
    label: "CSV",
    description: "Bulk file or spreadsheet-driven provisioning",
  },
];

export const ONBOARDING_STEPS = [
  {
    step: 1,
    key: "source",
    title: "Source discovery",
    prompt: "Where are users coming from?",
    /** Renders ONBOARDING_SOURCE_OPTIONS in the panel */
    type: "source_choice",
  },
  {
    step: 2,
    key: "groups",
    title: "Access mapping",
    prompt: "Which groups should they join by default?",
    type: "groups_text",
    placeholder: "List Okta groups (one per line or comma-separated), e.g. All Employees, VPN Users, App_Access_Standard",
    minRows: 5,
  },
  {
    step: 3,
    key: "policy",
    title: "Policy design",
    prompt: "Recommendations to confirm before submission",
    type: "policy_summary",
    recommendations: [
      {
        title: "Multi-factor authentication (MFA)",
        body:
          "Enroll new hires in phishing-resistant factors where possible (FIDO2/WebAuthn or Okta Verify). Use group-based MFA policies so contractors and admins can differ from the default employee population.",
      },
      {
        title: "Sign-on policy",
        body:
          "Tighten the org’s default sign-on policy for new joiners: require MFA at each session for cloud apps, and add a rule that challenges or blocks high-risk locations until the account is fully provisioned.",
      },
    ],
  },
];

export function getOnboardingStepDef(stepNumber) {
  return ONBOARDING_STEPS.find((s) => s.step === stepNumber) || null;
}

export function getSourceLabel(value) {
  const o = ONBOARDING_SOURCE_OPTIONS.find((x) => x.value === value);
  return o ? o.label : String(value || "");
}

/**
 * Single architect prompt when the user submits the completed wizard.
 */
export function buildOnboardingSubmitPrompt(answers) {
  const src = answers?.source || "unknown";
  const srcLabel = getSourceLabel(src);
  const groups = (answers?.groupsText || "").trim() || "(none specified)";
  const recs = ONBOARDING_STEPS[2]?.recommendations || [];
  const policyBlock = recs.map((r) => `- **${r.title}:** ${r.body}`).join("\n");

  return `[ONBOARDING_WIZARD_SUBMIT] The user completed the 3-step "Automate onboarding" guided wizard (static UI; no prior API calls for step transitions).

**Step 1 — Source discovery:** ${srcLabel} (\`${src}\`)

**Step 2 — Access mapping (default groups):**
${groups}

**Step 3 — Policy design (reviewed recommendations):**
${policyBlock}

Respond with JSON only. Summarize a practical onboarding plan: identity source integration, group assignments, MFA enrollment approach, and sign-on policy alignment. Include 3–4 **optionButtons** for sensible next actions (e.g. Terraform blueprint, staged rollout, security audit). If a concrete Okta JSON or HCL proposal fits, include **proposedConfig**.`;
}

export function loadOnboardingWizardFromStorage() {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_WIZARD_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") return null;
    if (o.wizardId !== ONBOARDING_WIZARD_ID) return null;
    if (typeof o.treePath !== "string" || typeof o.step !== "number") return null;
    return {
      wizardId: o.wizardId,
      treePath: o.treePath,
      step: o.step,
      answers: o.answers && typeof o.answers === "object" ? o.answers : {},
    };
  } catch {
    return null;
  }
}

export function saveOnboardingWizardToStorage(state) {
  try {
    if (!state) {
      sessionStorage.removeItem(ONBOARDING_WIZARD_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(
      ONBOARDING_WIZARD_STORAGE_KEY,
      JSON.stringify({
        wizardId: ONBOARDING_WIZARD_ID,
        treePath: state.treePath,
        step: state.step,
        answers: state.answers || {},
        savedAt: Date.now(),
      }),
    );
  } catch {
    /* ignore quota / private mode */
  }
}
